import React from "react";
import {
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherInfo,
} from "@subframe/core";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Table } from "../../../../ui/components/Table";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { useIsMonthClosed } from "../../../../shared/presentation/hooks";
import { dateToMonthYear } from "../../../../shared/utils/date";
import { truncateText } from "../../../../shared/utils/formatters";
import {
  CashBankPaginationEntity,
  CashBankTransactionEntity,
  CashBankTransactionType,
} from "../../domain";
import {
  formatCashBankAmount,
  formatCashBankDate,
  formatReconciledDate,
} from "../utils/cashBankFormatters";

interface CashBankTransactionTableProps {
  type: CashBankTransactionType;
  transactions: CashBankTransactionEntity[];
  pagination: CashBankPaginationEntity | null;
  loading: boolean;
  inlineLoading?: boolean;
  error: string | null;
  searchTerm: string;
  canMatch: boolean;
  canUnmatch: boolean;
  canSplit: boolean;
  onReview: (transaction: CashBankTransactionEntity) => void;
  onSplit: (transaction: CashBankTransactionEntity) => void;
  onView: (transaction: CashBankTransactionEntity) => void;
  onRetry: () => void;
  onPageChange: (page: number) => void;
}

const ClosedMonthBadge: React.FC<{ date: string }> = ({ date }) => {
  const month = date ? dateToMonthYear(date) : undefined;
  const { isClosed: isMonthClosed } = useIsMonthClosed(month);

  if (isMonthClosed !== true) {
    return null;
  }

  return (
    <Badge variant="neutral" className="shrink-0">
      Closed month
    </Badge>
  );
};

const PaginationControls: React.FC<{
  pagination: CashBankPaginationEntity;
  onPageChange: (page: number) => void;
}> = ({ pagination, onPageChange }) => {
  if (pagination.total_pages <= 1) {
    return null;
  }

  const pages = Array.from(
    { length: Math.min(5, pagination.total_pages) },
    (_, index) => {
      if (pagination.total_pages <= 5) {
        return index + 1;
      }

      if (pagination.current_page <= 3) {
        return index + 1;
      }

      if (pagination.current_page >= pagination.total_pages - 2) {
        return pagination.total_pages - 4 + index;
      }

      return pagination.current_page - 2 + index;
    },
  );

  return (
    <div className="flex w-full items-center justify-between mt-4">
      <span className="text-body font-body text-subtext-color">
        Showing {pagination.offset + 1} to{" "}
        {Math.min(pagination.offset + pagination.limit, pagination.total)} of{" "}
        {pagination.total} results
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="neutral-tertiary"
          size="small"
          icon={<FeatherChevronLeft />}
          disabled={pagination.current_page <= 1}
          onClick={() => onPageChange(pagination.current_page - 1)}
        >
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {pages.map((page) => (
            <Button
              key={page}
              variant={
                pagination.current_page === page
                  ? "brand-primary"
                  : "neutral-tertiary"
              }
              size="small"
              className="min-w-[32px]"
              onClick={() => onPageChange(page)}
            >
              {page}
            </Button>
          ))}
        </div>
        <Button
          variant="neutral-tertiary"
          size="small"
          icon={<FeatherChevronRight />}
          disabled={!pagination.has_more}
          onClick={() => onPageChange(pagination.current_page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const TABLE_STATUS_ROW_CLASS = "flex w-full items-center justify-center py-8";

const SplitTransactionBadge: React.FC<{
  transaction: CashBankTransactionEntity;
}> = ({ transaction }) => {
  const parentTransaction = transaction.parent_transaction;

  return (
    <span className="group/split relative inline-flex items-center">
      <span className="inline-flex items-center gap-1 text-caption-bold font-caption-bold text-success-700">
        <span>Split</span>

        <FeatherInfo className="text-caption font-caption text-neutral-400" />
      </span>
      <span className="pointer-events-none absolute left-0 top-8 z-30 hidden w-72 flex-col gap-2 rounded-md border border-solid border-neutral-border bg-default-background p-3 text-left shadow-lg group-hover/split:flex">
        <span className="text-caption-bold font-caption-bold text-default-font">
          Split from transaction
        </span>
        {parentTransaction ? (
          <>
            <span
              className="text-caption font-caption text-neutral-700"
              title={parentTransaction.description}
            >
              {truncateText(parentTransaction.description ?? "", 80)}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              {formatCashBankDate(parentTransaction.date)} ·{" "}
              {formatCashBankAmount(parentTransaction.amount)}
            </span>
          </>
        ) : (
          <span className="text-caption font-caption text-subtext-color">
            Parent transaction details are unavailable.
          </span>
        )}
      </span>
    </span>
  );
};

export const CashBankTransactionTable: React.FC<
  CashBankTransactionTableProps
> = ({
  type,
  transactions,
  pagination,
  loading,
  inlineLoading = false,
  error,
  searchTerm,
  canMatch,
  canUnmatch,
  canSplit,
  onReview,
  onSplit,
  onView,
  onRetry,
  onPageChange,
}) => {
  const amountClassName =
    type === "credit" ? "text-success-600" : "text-error-600";

  return (
    <div className="flex w-full flex-col items-start">
      <Table
        header={
          <Table.HeaderRow>
            <Table.HeaderCell className="h-8 w-32 flex-none">
              Date
            </Table.HeaderCell>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell className="h-8 w-40 flex-none">
              Amount
            </Table.HeaderCell>
            <Table.HeaderCell className="h-8 w-40 flex-none">
              Status
            </Table.HeaderCell>
            <Table.HeaderCell className="h-8 w-40 flex-none text-right">
              Action
            </Table.HeaderCell>
          </Table.HeaderRow>
        }
      >
        {loading ? (
          <TableSkeletonRows rowCount={5} columnCount={5} />
        ) : inlineLoading ? (
          <Table.Row>
            <Table.Cell colSpan={5}>
              <div className={TABLE_STATUS_ROW_CLASS}>
                <Loader />
              </div>
            </Table.Cell>
          </Table.Row>
        ) : error ? (
          <Table.Row>
            <Table.Cell colSpan={5} className="text-center py-12">
              <div className="flex flex-col items-center gap-2">
                <span className="text-body font-body text-error-600">
                  {error}
                </span>
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={onRetry}
                >
                  Retry
                </Button>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : transactions.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={5}>
              <div className={TABLE_STATUS_ROW_CLASS}>
                <span className="text-body font-body text-subtext-color">
                  {searchTerm.trim()
                    ? "No transactions match your search"
                    : "No transactions yet"}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : (
          transactions.map((transaction) => {
            const isReconciled = transaction.user_status === "Reconciled";
            const isSentForReview = transaction.review_status === "pending";
            const signedAmount =
              type === "debit"
                ? -Math.abs(transaction.amount)
                : transaction.amount;

            return (
              <Table.Row key={transaction.transaction_id}>
                <Table.Cell>
                  <span className="text-body font-body text-neutral-500">
                    {formatCashBankDate(transaction.date)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span
                    className="text-body-bold font-body-bold text-neutral-700"
                    title={transaction.description}
                  >
                    {truncateText(transaction.description ?? "", 80)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span
                    className={`text-body-bold font-body-bold ${amountClassName}`}
                  >
                    {signedAmount < 0 ? "-" : ""}
                    {formatCashBankAmount(signedAmount)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={isReconciled ? "success" : "warning"}
                        className={
                          isSentForReview
                            ? "border border-solid border-purple-200 bg-purple-50 [&>span]:text-purple-700"
                            : undefined
                        }
                      >
                        {isSentForReview
                          ? "Sent for Review"
                          : isReconciled
                            ? "Reconciled"
                            : "Pending"}
                      </Badge>
                      <ClosedMonthBadge date={transaction.date} />
                    </div>
                    {transaction.is_split && (
                      <SplitTransactionBadge transaction={transaction} />
                    )}
                    {isReconciled && transaction.updated_at && (
                      <span className="text-caption font-caption text-neutral-400">
                        Modified: {formatReconciledDate(transaction.updated_at)}
                      </span>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell className="text-right">
                  {isReconciled ? (
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      onClick={() => onView(transaction)}
                      disabled={!canUnmatch}
                    >
                      View
                    </Button>
                  ) : (
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        onClick={() => onReview(transaction)}
                        disabled={!canMatch}
                      >
                        Review
                      </Button>
                      {!transaction.is_split && (
                        <Button
                          variant="brand-tertiary"
                          size="small"
                          onClick={() => onSplit(transaction)}
                          disabled={
                            !canSplit || transaction.review_status === "pending"
                          }
                        >
                          Split
                        </Button>
                      )}
                    </div>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })
        )}
      </Table>
      {pagination && !loading && !inlineLoading && (
        <PaginationControls
          pagination={pagination}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};
